import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

function randomBetween(min, max) {
    return Math.random() * (max - min) + min;
}

// 手機版視窗較窄，範圍比例照抄桌機數值的話可飄動的實際寬度會太侷促，
// 因此窄螢幕時加大比例，讓花朵在畫面中有更明顯的左右飄動空間
function getEffectiveSpawnRangeRatio(baseRatio) {
    if (typeof window === 'undefined') return baseRatio;
    return window.innerWidth < 768 ? 0.9 : baseRatio;
}

/**
 * 飄落單張照片特效
 * Falling single photo effect
 *
 * 核心思路參考成熟的落葉飄落實作（qtqz/natural-falling-effect、Sarah Higley
 * "Falling Leaves"）：水平與旋轉速度是每片重生時「隨機決定一次的固定值」，
 * 之後單純逐幀累加，不是用正弦波之類的週期函數每幀重新計算。之前的版本用
 * 週期函數驅動風力，不管疊加幾層頻率，本質上還是週期性的，肉眼看得出規律的
 * 「左、右、左、右」循環；改成固定隨機速度後，每片花瓣就是各自筆直、角度
 * 各異地飄落，沒有任何週期可循，反而更接近真實落葉隨機飄動的樣子。
 *
 * 水平邊界是穿越式的（wrap-around）：飄出範圍左緣就直接從右緣繼續、反之亦然，
 * 不做任何反彈或回中拉力，這點也是直接沿用參考實作的做法。
 *
 * 花瓣本體是平面照片，只做 2D 平面旋轉（rotateZ），不套用 rotateX/rotateY 或
 * perspective 透視——那樣的 3D 翻轉對「一張平面照片」來說視覺上會顯得失真詭異，
 * 因為照片本身沒有厚度和背面內容可供 3D 翻面時顯示。
 *
 * spin=false（如完整花朵照片）水平速度與旋轉幅度收斂到較小範圍；
 * spin=true（如葉子）水平速度與旋轉幅度較大，模擬葉片更容易被風吹動翻轉的樣子。
 *
 * spawnRangeRatio 控制水平生成範圍佔容器寬度的比例（置中），只在畫面中間一段
 * 範圍內飄落，而非佔滿整個寬度。
 */
const FallingWindPhoto = ({
    imageSrc,
    count = 1,
    size = 90,
    fallSpeed = 0.85,
    windMagnitude = 1.1,
    spawnRangeRatio = 0.55,
    spin = false,
    maxSwayAngle = 15,
    className = '',
}) => {
    const containerRef = useRef(null);
    const leavesRef = useRef([]);
    const rafRef = useRef(null);

    const leaves = useMemo(() => Array.from({ length: count }, (_, i) => ({ id: i })), [count]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        // 蓮花整株較大，水平速度與旋轉幅度不宜太誇張；菩提葉較輕巧，可以明顯大一點
        const xSpeedRange = windMagnitude * (spin ? 1.4 : 0.8);
        const rotSpeedRange = spin ? 0.03 : 0.012;
        const maxTiltAngle = spin ? 180 : maxSwayAngle;

        const resetLeaf = (el, initial) => {
            const width = container.clientWidth;
            const height = container.clientHeight;
            const rangeWidth = width * getEffectiveSpawnRangeRatio(spawnRangeRatio);
            const rangeStart = (width - rangeWidth) / 2;
            return {
                el,
                x: rangeStart + randomBetween(0, rangeWidth),
                // 一律從畫面最上方開始飄落（不會一開始就出現在畫面中間），
                // 初次生成時用隨機負值稍微錯開高度，避免多片同時從同一點掉下來
                y: initial ? -size - randomBetween(0, height) : -size,
                // 水平速度、下墜速度、旋轉速度都是重生時隨機決定一次的固定值，
                // 之後單純逐幀累加，不會有週期性規律
                xSpeed: randomBetween(-xSpeedRange, xSpeedRange),
                ySpeed: randomBetween(0.5, 1.4) * fallSpeed,
                rotSpeed: randomBetween(-rotSpeedRange, rotSpeedRange) * (180 / Math.PI),
                rotZ: spin ? randomBetween(-maxTiltAngle, maxTiltAngle) : 0,
                opacity: randomBetween(0.85, 1),
            };
        };

        leavesRef.current = Array.from(container.children).map((el) => resetLeaf(el, true));

        const animate = () => {
            const width = container.clientWidth;
            const height = container.clientHeight;
            const rangeWidth = width * getEffectiveSpawnRangeRatio(spawnRangeRatio);
            const rangeStart = (width - rangeWidth) / 2;

            for (const leaf of leavesRef.current) {
                leaf.x += leaf.xSpeed;
                leaf.y += leaf.ySpeed;
                leaf.rotZ += leaf.rotSpeed;

                if (!spin) {
                    // 花朵傾斜角度限制在 ±maxTiltAngle 之間，超出就反轉旋轉方向，
                    // 模擬鐘擺式小幅來回擺動而非整圈旋轉
                    if (leaf.rotZ > maxTiltAngle || leaf.rotZ < -maxTiltAngle) {
                        leaf.rotSpeed = -leaf.rotSpeed;
                    }
                }

                // 水平邊界是穿越式的：飄出範圍就直接從另一側繼續，不反彈也不拉回
                if (leaf.x > rangeStart + rangeWidth) {
                    leaf.x = rangeStart;
                } else if (leaf.x < rangeStart) {
                    leaf.x = rangeStart + rangeWidth;
                }

                if (leaf.y > height + size) {
                    Object.assign(leaf, resetLeaf(leaf.el, false));
                }

                leaf.el.style.transform = `translate3d(${leaf.x}px, ${leaf.y}px, 0) rotateZ(${leaf.rotZ}deg)`;
                leaf.el.style.opacity = leaf.opacity;
            }

            rafRef.current = requestAnimationFrame(animate);
        };
        rafRef.current = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(rafRef.current);
        };
    }, [count, fallSpeed, windMagnitude, spawnRangeRatio, spin, size, maxSwayAngle]);

    return (
        <div
            ref={containerRef}
            className={className}
            aria-hidden="true"
            style={{
                position: 'absolute',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 5,
            }}
        >
            {leaves.map((leaf) => (
                <img
                    key={leaf.id}
                    src={imageSrc}
                    alt=""
                    width={size}
                    height={size}
                    draggable={false}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        display: 'block',
                        willChange: 'transform',
                        filter: 'drop-shadow(0 6px 10px rgba(0, 0, 0, 0.2))',
                    }}
                />
            ))}
        </div>
    );
};

FallingWindPhoto.propTypes = {
    imageSrc: PropTypes.string.isRequired,
    count: PropTypes.number,
    size: PropTypes.number,
    fallSpeed: PropTypes.number,
    windMagnitude: PropTypes.number,
    spawnRangeRatio: PropTypes.number,
    spin: PropTypes.bool,
    maxSwayAngle: PropTypes.number,
    className: PropTypes.string,
};

export default FallingWindPhoto;
